/**
 * ч.1334: стаття довідки в панелі платформи — текст, правка, доручення агенту, історія.
 *
 * - «Текст»: як бачить користувач (markdown) + службові правила для агента підтримки; «редагувати»
 *   відкриває поля і перегляд. Збереження — з версією статті: хтось змінив раніше — 409, а не
 *   мовчазне затирання.
 * - «Доручити агенту»: людина пише, що змінити; агент (бачить усю довідку) читає статтю, сторінку з
 *   її питаннями і суміжні статті й пропонує текст. Видно, що змінено, сумніви, які статті варто
 *   узгодити, і різницю; «прийняти» застосовує, «уточнити» — переписати ще раз з поправкою.
 * - Розробники змінили статтю в базі знань, а її вже правили тут — банер: взяти новий текст, лишити
 *   свій або доручити агенту обʼєднати.
 * - «Історія»: кожна версія (хто, звідки), різниця з поточною, «повернути цю версію».
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import {
  KIND_LABEL, SOURCE_LABEL, supportHelp,
  type ArticleText, type HelpArticle, type RevisionRow,
} from '@/api/supportHelp';
import { useConfirm } from '@/components/ConfirmProvider';
import { renderMarkdown } from '@/pages/help/markdown';
import { extractApiError } from '@/utils/extractApiError';
import { diffStat, lineDiff, type DiffLine } from './lineDiff';
import { Tag, fmtWhen } from './SupportHelpPage';

const ACTIVE = new Set(['QUEUED', 'RUNNING', 'DONE', 'FAILED']);

function Md({ text }: { text: string }) {
  const html = useMemo(() => renderMarkdown(text || ''), [text]);
  // Вміст екранується у renderMarkdown (як на сторінці довідки).
  return <div className="help-md" dangerouslySetInnerHTML={{ __html: html }} />;
}

/** Різниця за рядками; довгі незмінні шматки згорнуто (видно 2 рядки контексту). */
export function DiffView({ before, after }: { before: string; after: string }) {
  const lines = useMemo(() => lineDiff(before, after), [before, after]);
  const [open, setOpen] = useState<Set<number>>(new Set());
  const stat = diffStat(lines);
  if (!stat.add && !stat.del) return <p className="sh-muted">Текст не змінився.</p>;
  const near = lines.map((_, i) => lines.slice(Math.max(0, i - 2), i + 3).some((l) => l.kind !== 'same'));
  const out: React.ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    if (!near[i]) {
      const start = i;
      while (i < lines.length && !near[i]) i += 1;
      if (!open.has(start)) {
        out.push(
          <button key={`g${start}`} type="button" className="btn-link sh-diff__gap"
            onClick={() => setOpen(new Set(open).add(start))}>
            … {i - start} рядків без змін — показати
          </button>);
        continue;
      }
      for (let k = start; k < i; k += 1) out.push(<DiffRow key={k} line={lines[k]!} />);
      continue;
    }
    out.push(<DiffRow key={i} line={lines[i]!} />);
    i += 1;
  }
  return (
    <div className="sh-diff" aria-label="Різниця текстів">
      <div className="sh-muted" style={{ marginBottom: '0.3rem' }}>
        <Tag tone="green">+ {stat.add} рядків</Tag><Tag tone="red">− {stat.del} рядків</Tag>
      </div>
      {out}
    </div>
  );
}

function DiffRow({ line }: { line: DiffLine }) {
  const sign = line.kind === 'add' ? '+' : line.kind === 'del' ? '−' : ' ';
  return <div className={`sh-diff__line sh-diff__line--${line.kind}`}>{sign} {line.text || ' '}</div>;
}

/** Поля, що відрізняються у варіанті від статті. */
function changedFields(a: ArticleText, b: ArticleText): (keyof ArticleText)[] {
  return (['title', 'summary', 'body_md', 'agent_note'] as const)
    .filter((k) => (a[k] ?? '').trim() !== (b[k] ?? '').trim());
}

const FIELD_LABEL: Record<keyof ArticleText, string> = {
  title: 'Назва', summary: 'Коротко', body_md: 'Текст', agent_note: 'Службові правила для агента підтримки',
};

function TextDiff({ before, after }: { before: ArticleText; after: ArticleText }) {
  const fields = changedFields(before, after);
  if (!fields.length) return <p className="sh-muted">Текст не змінився.</p>;
  return (
    <>
      {fields.map((k) => (
        <div key={k} style={{ marginBottom: '0.6rem' }}>
          <div className="sh-label">{FIELD_LABEL[k]}</div>
          <DiffView before={before[k] ?? ''} after={after[k] ?? ''} />
        </div>
      ))}
    </>
  );
}

function TextView({ a }: { a: ArticleText & { kind?: string } }) {
  return (
    <>
      {a.summary && a.kind !== 'question' && <p className="sh-muted" style={{ marginTop: 0 }}>{a.summary}</p>}
      <Md text={a.body_md} />
      <details className="sh-note" open={!!a.agent_note}>
        <summary>Службові правила для агента підтримки {a.agent_note ? '' : '(немає)'}</summary>
        {a.agent_note
          ? <div style={{ whiteSpace: 'pre-wrap' }}>{a.agent_note}</div>
          : <p className="sh-muted">Клієнт їх не бачить; агент підтримки враховує їх у відповідях.</p>}
      </details>
    </>
  );
}

// ── текст і правка ─────────────────────────────────────────────────────────────────────────────

function ArticleBlock({ article, onSaved, busy }: { article: HelpArticle; onSaved: (a: HelpArticle) => void; busy: boolean }) {
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [preview, setPreview] = useState(false);
  const [form, setForm] = useState<ArticleText>(article);
  const [err, setErr] = useState('');
  useEffect(() => { if (!editing) setForm(article); }, [article, editing]);
  const dirty = changedFields(article, form).length > 0;
  const save = useMutation({
    mutationFn: () => {
      const patch: Partial<ArticleText> = {};
      for (const k of changedFields(article, form)) patch[k] = form[k] ?? '';
      return supportHelp.edit(article.id, article.version, patch);
    },
    onSuccess: (a) => { setEditing(false); setErr(''); onSaved(a); },
    onError: (e) => setErr(extractApiError(e, 'Не вдалося зберегти статтю')),
  });
  const set = (k: keyof ArticleText) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [k]: e.target.value });

  if (!editing) {
    return (
      <div className="card">
        <div className="toolbar" style={{ justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>Текст статті</h3>
          <button type="button" disabled={busy} onClick={() => setEditing(true)}
            title={busy ? 'Агент працює над статтею — дочекайтесь результату' : undefined}>редагувати</button>
        </div>
        <TextView a={article} />
      </div>
    );
  }
  return (
    <div className="card">
      <div className="toolbar" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Правка статті</h3>
        <div className="seg-tabs" style={{ margin: 0 }}>
          <button type="button" className={!preview ? 'active' : ''} onClick={() => setPreview(false)}>текст</button>
          <button type="button" className={preview ? 'active' : ''} onClick={() => setPreview(true)}>
            як побачить користувач</button>
        </div>
      </div>
      {preview ? <TextView a={{ ...form, kind: article.kind }} /> : (
        <>
          <div className="field">
            <label htmlFor="sh-title">{article.kind === 'question' ? 'Питання' : 'Назва'}</label>
            <input id="sh-title" value={form.title} onChange={set('title')} maxLength={300} />
          </div>
          <div className="field">
            <label htmlFor="sh-summary">Коротко (для пошуку і карти довідки)</label>
            <textarea id="sh-summary" rows={3} value={form.summary} onChange={set('summary')} />
          </div>
          <div className="field">
            <label htmlFor="sh-body">{article.kind === 'question' ? 'Відповідь' : 'Текст'} (markdown: «## розділ», «1. крок», «- пункт», **жирний**)</label>
            <textarea id="sh-body" rows={22} className="sh-code" value={form.body_md} onChange={set('body_md')} />
          </div>
          <div className="field">
            <label htmlFor="sh-note">Службові правила для агента підтримки (клієнт не бачить)</label>
            <textarea id="sh-note" rows={5} value={form.agent_note ?? ''} onChange={set('agent_note')} />
          </div>
        </>
      )}
      {err && <div className="error">{err}</div>}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
        <button type="button" className="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'зберігаю…' : 'зберегти'}</button>
        <button type="button" onClick={async () => {
          if (dirty && !(await confirm('Є незбережені правки. Вийти без збереження?'))) return;
          setEditing(false); setErr('');
        }}>скасувати</button>
      </div>
    </div>
  );
}

// ── доручення агенту ───────────────────────────────────────────────────────────────────────────

function AgentBlock({ article, onApplied }: { article: HelpArticle; onApplied: (a: HelpArticle) => void }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const latest = article.drafts.find((d) => ACTIVE.has(d.status));
  const draft = useQuery({
    queryKey: ['support-help-draft', latest?.id],
    queryFn: () => supportHelp.draft(latest!.id),
    enabled: !!latest,
    refetchInterval: (q) => (q.state.data && ['QUEUED', 'RUNNING'].includes(q.state.data.status) ? 3000 : false),
  });
  const d = draft.data;
  // Агент закінчив — статтю перечитати (список доручень, стан у переліку).
  useEffect(() => {
    if (d && !['QUEUED', 'RUNNING'].includes(d.status) && latest && ['QUEUED', 'RUNNING'].includes(latest.status)) {
      qc.invalidateQueries({ queryKey: ['support-help-article', article.id] });
      qc.invalidateQueries({ queryKey: ['support-help'] });
    }
  }, [d, latest, qc, article.id]);
  const [task, setTask] = useState('');
  const [refine, setRefine] = useState('');
  const [view, setView] = useState<'diff' | 'text'>('diff');
  const [err, setErr] = useState('');
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['support-help-article', article.id] });
    qc.invalidateQueries({ queryKey: ['support-help'] });
  };
  const delegate = useMutation({
    mutationFn: (p: { text: string; parent?: string }) =>
      supportHelp.delegate(article.id, p.text, p.parent ? { parent_draft_id: p.parent } : {}),
    onSuccess: () => { setTask(''); setRefine(''); setErr(''); refresh(); },
    onError: (e) => setErr(extractApiError(e, 'Не вдалося доручити агенту')),
  });
  const accept = useMutation({
    mutationFn: (id: string) => supportHelp.accept(id),
    onSuccess: (a) => { setErr(''); onApplied(a); qc.invalidateQueries({ queryKey: ['support-help'] }); },
    onError: (e) => setErr(extractApiError(e, 'Не вдалося застосувати текст агента')),
  });
  const reject = useMutation({
    mutationFn: (id: string) => supportHelp.reject(id),
    onSuccess: () => { setErr(''); refresh(); },
    onError: (e) => setErr(extractApiError(e, 'Не вдалося відхилити')),
  });

  const form = (
    <>
      <div className="field">
        <label htmlFor="sh-task">Що змінити в статті</label>
        <textarea id="sh-task" rows={5} value={task} onChange={(e) => setTask(e.target.value)}
          placeholder={'Наприклад: «Додай, що спершу треба зберегти документ, а потім провести» або «Перепиши '
            + 'коротше і простіше, без повторів статті про списки»'} />
      </div>
      <button type="button" className="primary" disabled={task.trim().length < 3 || delegate.isPending}
        onClick={() => delegate.mutate({ text: task })}>доручити агенту</button>
    </>
  );

  let body: React.ReactNode = form;
  if (latest && !d) body = <p className="sh-muted">Завантаження…</p>;
  if (d && (d.status === 'QUEUED' || d.status === 'RUNNING')) {
    body = (
      <div className="sh-agent-wait" role="status">
        <p style={{ margin: 0 }}><b>Агент працює</b> — читає статтю, її сторінку з питаннями і суміжні статті,
          потім пише текст. Зазвичай до хвилини; сторінку можна не тримати відкритою.</p>
        <p className="sh-muted" style={{ marginBottom: 0 }}>Завдання: {d.instruction}</p>
      </div>
    );
  }
  if (d && d.status === 'FAILED') {
    body = (
      <>
        <div className="error">{d.error}</div>
        <p className="sh-muted">Завдання: {d.instruction}</p>
        <button type="button" onClick={() => reject.mutate(d.id)} disabled={reject.isPending}>закрити</button>
      </>
    );
  }
  if (d && d.status === 'DONE') {
    const stale = d.article_version !== d.base_version;
    const proposed: ArticleText = { title: d.title ?? '', summary: d.summary ?? '', body_md: d.body_md ?? '',
      agent_note: d.agent_note };
    body = (
      <>
        <p className="sh-muted sh-task" style={{ marginTop: 0 }}>
          Завдання: {d.instruction}
          {d.cost_usd != null && <> · ${d.cost_usd.toFixed(2)}</>}
          {d.read_slugs.length > 0 && <> · прочитано статей: {d.read_slugs.length}</>}
        </p>
        {d.declined ? (
          <>
            <div className="sh-box sh-box--amber"><b>Агент не змінював текст.</b>
              <div style={{ whiteSpace: 'pre-wrap', marginTop: '0.3rem' }}>{d.changes}</div></div>
            <button type="button" onClick={() => reject.mutate(d.id)} disabled={reject.isPending}>закрити</button>
          </>
        ) : (
          <>
            <div className="sh-label">Що змінено</div>
            <div style={{ whiteSpace: 'pre-wrap', marginBottom: '0.5rem' }}>{d.changes}</div>
            {d.doubts && (
              <div className="sh-box sh-box--amber"><b>Перевірте:</b>
                <div style={{ whiteSpace: 'pre-wrap', marginTop: '0.3rem' }}>{d.doubts}</div></div>
            )}
            {d.related.length > 0 && (
              <div style={{ margin: '0.5rem 0' }}>
                <div className="sh-label">Варто узгодити</div>
                <ul style={{ margin: '0.2rem 0 0', paddingLeft: '1.1rem' }}>
                  {d.related.map((r) => (
                    <li key={r.id}><Link to={`/platform/support-help/${r.id}`}>{r.title}</Link> — {r.reason}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="seg-tabs">
              <button type="button" className={view === 'diff' ? 'active' : ''} onClick={() => setView('diff')}>
                що змінилось</button>
              <button type="button" className={view === 'text' ? 'active' : ''} onClick={() => setView('text')}>
                як побачить користувач</button>
            </div>
            {view === 'diff' ? <TextDiff before={article} after={proposed} />
              : <TextView a={{ ...proposed, kind: article.kind }} />}
            {stale && (
              <div className="sh-box sh-box--amber">Статтю змінили після того, як агент почав роботу. Прийняти
                цей варіант не можна — доручіть ще раз, агент візьме поточний текст.</div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0.6rem 0' }}>
              <button type="button" className="primary" disabled={stale || accept.isPending}
                title={stale ? 'Статтю змінили після доручення' : undefined}
                onClick={async () => {
                  if (await confirm('Застосувати текст агента? Користувачі й агент підтримки одразу побачать новий '
                    + 'текст; попередню версію можна повернути в «Історії».')) accept.mutate(d.id);
                }}>прийняти</button>
              <button type="button" disabled={reject.isPending} onClick={() => reject.mutate(d.id)}>відхилити</button>
            </div>
            <div className="field">
              <label htmlFor="sh-refine">Уточнити — агент перепише цей варіант з поправкою</label>
              <textarea id="sh-refine" rows={3} value={refine} onChange={(e) => setRefine(e.target.value)}
                placeholder="Наприклад: «залиш розділ про права як був» або «коротше, без списку»" />
            </div>
            <button type="button" disabled={refine.trim().length < 3 || delegate.isPending}
              onClick={() => delegate.mutate({ text: refine, parent: d.id })}>переписати з уточненням</button>
          </>
        )}
      </>
    );
  }
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Доручити агенту</h3>
      <p className="sh-muted" style={{ marginTop: 0 }}>
        Агент бачить усю довідку: прочитає цю статтю, її сторінку з усіма питаннями і суміжні статті, щоб новий
        текст їм не суперечив, і запропонує варіант. Застосовуєте ви.
      </p>
      {body}
      {err && <div className="error">{err}</div>}
    </div>
  );
}

// ── оновлення з бази знань ─────────────────────────────────────────────────────────────────────

function KbBanner({ article, onDone }: { article: HelpArticle; onDone: (a: HelpArticle) => void }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [show, setShow] = useState(false);
  const [err, setErr] = useState('');
  const p = article.kb_pending!;
  const decide = useMutation({
    mutationFn: (action: 'accept' | 'keep' | 'archive') => supportHelp.kb(article.id, action, article.version),
    onSuccess: (a) => { setErr(''); onDone(a); qc.invalidateQueries({ queryKey: ['support-help'] }); },
    onError: (e) => setErr(extractApiError(e, 'Не вдалося')),
  });
  const merge = useMutation({
    mutationFn: () => supportHelp.delegate(article.id,
      'Обʼєднай новий текст бази знань з правками, внесеними на платформі.', { merge_kb: true }),
    onSuccess: () => {
      setErr('');
      qc.invalidateQueries({ queryKey: ['support-help-article', article.id] });
      qc.invalidateQueries({ queryKey: ['support-help'] });
    },
    onError: (e) => setErr(extractApiError(e, 'Не вдалося доручити агенту')),
  });
  if (p.removed) {
    return (
      <div className="sh-box sh-box--red" role="alert">
        <b>У базі знань цієї статті більше немає.</b> Розробники прибрали або перейменували її; тут вона
        лишилась, бо її змінювали на платформі.
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
          <button type="button" className="danger" disabled={decide.isPending} onClick={async () => {
            if (await confirm('Прибрати статтю з довідки? Користувачі й агент підтримки її більше не побачать.')) {
              decide.mutate('archive');
            }
          }}>прибрати з довідки</button>
          <button type="button" disabled={decide.isPending} onClick={() => decide.mutate('keep')}>
            лишити як власну статтю</button>
        </div>
        {err && <div className="error">{err}</div>}
      </div>
    );
  }
  const kbText: ArticleText = { title: p.title ?? '', summary: p.summary ?? '', body_md: p.body_md ?? '',
    agent_note: p.agent_note ?? null };
  return (
    <div className="sh-box sh-box--amber" role="alert">
      <b>У базі знань оновився текст цієї статті.</b> Розробники змінили її після правок на платформі; ваші
      правки не затерто. Вирішіть, що лишити.
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
        <button type="button" className="btn-link" onClick={() => setShow(!show)}>
          {show ? 'сховати різницю' : 'показати різницю'}</button>
        <button type="button" disabled={decide.isPending} onClick={async () => {
          if (await confirm('Взяти текст бази знань? Правки, внесені на платформі, зникнуть із статті (лишаться '
            + 'в «Історії»).')) decide.mutate('accept');
        }}>взяти текст бази знань</button>
        <button type="button" disabled={decide.isPending} onClick={() => decide.mutate('keep')}>лишити мій</button>
        <button type="button" className="primary" disabled={merge.isPending || article.drafts.some(
          (d) => d.status === 'QUEUED' || d.status === 'RUNNING')} onClick={() => merge.mutate()}>
          доручити агенту обʼєднати</button>
      </div>
      {show && <div style={{ marginTop: '0.6rem' }}><TextDiff before={article} after={kbText} /></div>}
      {err && <div className="error">{err}</div>}
    </div>
  );
}

// ── історія ────────────────────────────────────────────────────────────────────────────────────

function History({ article, onDone }: { article: HelpArticle; onDone: (a: HelpArticle) => void }) {
  const confirm = useConfirm();
  const [sel, setSel] = useState<RevisionRow | null>(article.revisions[0] ?? null);
  const rev = useQuery({ queryKey: ['support-help-revision', sel?.id], queryFn: () => supportHelp.revision(sel!.id),
    enabled: !!sel });
  const [err, setErr] = useState('');
  const revert = useMutation({
    mutationFn: () => supportHelp.revert(article.id, sel!.id, article.version),
    onSuccess: (a) => { setErr(''); onDone(a); },
    onError: (e) => setErr(extractApiError(e, 'Не вдалося повернути версію')),
  });
  if (!article.revisions.length) {
    return <div className="card"><p className="sh-muted" style={{ margin: 0 }}>
      Статтю ще не змінювали на платформі — текст такий, як у базі знань.</p></div>;
  }
  const same = rev.data && changedFields(article, rev.data).length === 0;
  return (
    <div className="sh-grid">
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Версії</h3>
        <ul className="sh-revs">
          {article.revisions.map((r) => (
            <li key={r.id}>
              <button type="button" className={`btn-link${sel?.id === r.id ? ' sh-revs__sel' : ''}`}
                onClick={() => setSel(r)}>
                {fmtWhen(r.created_at)} · {SOURCE_LABEL[r.source]}{r.user_email ? ` · ${r.user_email}` : ''}
              </button>
              {r.note && <div className="sh-muted">{r.note}</div>}
            </li>
          ))}
        </ul>
      </div>
      <div className="card">
        {!rev.data ? <p className="sh-muted">Завантаження…</p> : (
          <>
            <div className="toolbar" style={{ justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>Версія від {fmtWhen(rev.data.created_at)}</h3>
              <button type="button" disabled={!!same || revert.isPending}
                title={same ? 'Це і є поточний текст' : undefined}
                onClick={async () => {
                  if (await confirm('Повернути цю версію? Поточний текст лишиться в історії.')) revert.mutate();
                }}>повернути цю версію</button>
            </div>
            {err && <div className="error">{err}</div>}
            <div className="sh-label">Відмінності від поточного тексту</div>
            <TextDiff before={article} after={rev.data} />
            <details style={{ marginTop: '0.6rem' }}>
              <summary>текст цієї версії</summary>
              <TextView a={{ ...rev.data, kind: article.kind }} />
            </details>
          </>
        )}
      </div>
    </div>
  );
}

// ── сторінка ───────────────────────────────────────────────────────────────────────────────────

export default function SupportHelpArticlePage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['support-help-article', id], queryFn: () => supportHelp.get(id) });
  const [tab, setTab] = useState<'text' | 'history'>('text');
  const a = q.data;
  const put = (next: HelpArticle) => {
    qc.setQueryData(['support-help-article', id], next);
    qc.invalidateQueries({ queryKey: ['support-help'] });
  };
  if (q.isLoading) return <div className="card"><p>Завантаження…</p></div>;
  if (!a) return <div className="card"><div className="error">{extractApiError(q.error, 'Статтю не знайдено')}</div></div>;
  const busy = a.drafts.some((d) => d.status === 'QUEUED' || d.status === 'RUNNING');
  return (
    <>
      <div className="card">
        <Link to="/platform/support-help" style={{ fontSize: '0.85rem' }}>← усі статті</Link>
        <h2 style={{ margin: '0.4rem 0 0.5rem' }}>{a.title}</h2>
        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'flex', flexWrap: 'wrap',
          gap: '0.4rem 1rem', alignItems: 'center' }}>
          <span>{KIND_LABEL[a.kind]} · розділ «{a.section}»</span>
          {a.agent_only && <span title="Текст влито в статтю сторінки; клієнт його не бачить, агенти читають">лише для агента</span>}
          {a.route_pattern && <span>сторінка /{a.route_pattern}</span>}
          {a.parent && <span>{a.kind === 'question' ? 'питання до ' : ''}
            <Link to={`/platform/support-help/${a.parent.id}`}>{a.parent.title}</Link></span>}
          {a.status === 'published'
            ? <a href={`/help/${a.slug}`} target="_blank" rel="noreferrer">відкрити в довідці</a>
            : <Tag tone="red">прибрано з довідки</Tag>}
          <span>{a.own ? <Tag tone="green">власна стаття</Tag>
            : a.version > 1 ? <Tag tone="green">змінено на платформі</Tag> : <Tag tone="blue">як у базі знань</Tag>}
          {a.edited_at && <>змінено {fmtWhen(a.edited_at)}{a.edited_by ? ` · ${a.edited_by}` : ''}</>}</span>
        </div>
        {a.videos.length > 0 && (
          <p className="sh-muted" style={{ marginBottom: 0 }}>
            До статті є відео-інструкції ({a.videos.map((v) => v.title).join('; ')}). Після зміни тексту редактор
            відео побачить позначку «довідка змінилась».
          </p>
        )}
        {a.kb_pending && <div style={{ marginTop: '0.6rem' }}><KbBanner article={a} onDone={put} /></div>}
        <div className="seg-tabs">
          <button type="button" className={tab === 'text' ? 'active' : ''} onClick={() => setTab('text')}>текст</button>
          <button type="button" className={tab === 'history' ? 'active' : ''} onClick={() => setTab('history')}>
            історія ({a.revisions.length})</button>
        </div>
      </div>
      {tab === 'text' ? (
        <div className="sh-grid">
          <div>
            <ArticleBlock article={a} onSaved={put} busy={busy} />
            {a.questions.length > 0 && (
              <div className="card">
                <h3 style={{ marginTop: 0 }}>Питання-відповіді до цієї {a.kind === 'topic' ? 'теми' : 'сторінки'} ({a.questions.length})</h3>
                <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                  {a.questions.map((x) => (
                    <li key={x.id}><Link to={`/platform/support-help/${x.id}`}>{x.title}</Link>
                      {(x.version ?? 1) > 1 && <> <Tag tone="green">змінено тут</Tag></>}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <AgentBlock article={a} onApplied={put} />
        </div>
      ) : <History article={a} onDone={put} />}
    </>
  );
}
